import type { OsmLayer } from '@/models/interfaces/map/OsmLayer'
import { OSM_LAYERS_CONFIG } from '@/models/interfaces/map/OsmLayer'
import type { Layer } from '@/models/interfaces/map/Layer'
import type Map from '@/components/map/Map.vue'
import maplibregl from 'maplibre-gl'
import { martinTilesBaseUrl } from '@/config/env'
import LayerService from '../LayerService'
import { AtlasCategoriesService } from './AtlasCategoriesService'
import type { AtlasCategory } from '@/models/interfaces/map/AtlasCategory'
import { libelleDynamique } from '@/utils/libelleDynamique'

/**
 * Service pour gérer les couches OSM provenant du serveur Martin
 */
export class OsmLayersService {
  private static martinBaseUrl: string = ''
  private static initializedLayers: OsmLayer[] = []

  /**
   * Initialise le service avec l'URL de base de Martin
   */
  static initialize(martinUrl?: string) {
    this.martinBaseUrl = martinUrl || martinTilesBaseUrl || ''

    if (!this.martinBaseUrl) {
      console.warn(
        "VITE_MARTIN_TILES_URL n'est pas définie. Les couches OSM ne seront pas disponibles.",
      )
      return
    }

    // Initialiser les couches avec l'URL Martin
    this.initializedLayers = OSM_LAYERS_CONFIG.map((layer) => ({
      ...layer,
      martinUrl: this.martinBaseUrl,
    }))
  }

  /**
   * Retourne la liste des couches OSM disponibles
   * Note: Retourne des Layer car LayerService.initLayer retourne Layer, pas OsmLayer
   */
  static getAvailableLayers(): Layer[] {
    if (!this.martinBaseUrl && !this.initializedLayers.length) {
      this.initialize()
    }
    return this.initializedLayers.map((layer) =>
      LayerService.initLayer(layer, layer.isShown ?? false),
    )
  }

  /**
   * Construit l'URL des tuiles pour une couche donnée
   * Si categoryCode est fourni, utilise les fonctions filtrées par catégorie
   */
  static getTilesUrl(layer: OsmLayer, categoryCode?: string): string {
    if (!this.martinBaseUrl) {
      this.initialize()
    }
    const baseUrl = layer.martinUrl || this.martinBaseUrl

    // Si categoryCode est fourni, utiliser les fonctions filtrées par catégorie
    if (categoryCode) {
      // Mapper le geometryType vers le nom de fonction correspondant
      const functionNameMap: Record<string, string> = {
        point: 'vector_tile_planet_osm_point_by_category',
        line: 'vector_tile_planet_osm_line_by_category',
        polygon: 'vector_tile_planet_osm_polygon_by_category',
        roads: 'vector_tile_planet_osm_roads_by_category',
      }

      const functionName =
        functionNameMap[layer.geometryType] || 'vector_tile_planet_osm_line_by_category'

      // Format: {baseUrl}/{functionName}/{z}/{x}/{y}?category_code={categoryCode}
      // Note: Ne pas inclure .pbf dans l'URL, Martin le gère automatiquement
      return `${baseUrl}/${functionName}/{z}/{x}/{y}?category_code=${encodeURIComponent(categoryCode)}`
    }

    // Format standard Martin: {baseUrl}/{tableName}/{z}/{x}/{y}
    // Note: Ne pas inclure .pbf dans l'URL, Martin le gère automatiquement
    const cleanBaseUrl = (baseUrl as string).split('?')[0].replace(/\/+$/, '')
    return `${cleanBaseUrl}/${layer.tableName}/{z}/{x}/{y}`
  }

  /**
   * Ajoute une couche OSM à la carte
   */
  static async addOsmLayerToMap(
    myMap: InstanceType<typeof Map> | undefined,
    layer: OsmLayer,
  ): Promise<void> {
    if (!myMap?.map) {
      console.error("La carte n'est pas initialisée")
      return
    }

    const map = myMap.map
    const sourceId = layer.id.toString()
    const tilesUrl = this.getTilesUrl(layer)

    // Vérifier si la source existe déjà
    if (map.getSource(sourceId)) {
      console.warn(`La source ${sourceId} existe déjà`)
      return
    }

    // Ajouter la source de tuiles vecteurs
    myMap.addVectorTileSource(sourceId, tilesUrl, 0, 22)

    // Attendre que la source soit chargée (pas besoin d'attendre, MapLibre gère cela automatiquement)
    // Mais on attend que la carte soit prête
    if (!map.loaded()) {
      await new Promise<void>((resolve) => {
        map.once('load', () => resolve())
      })
    }

    // Déterminer le type de layer selon le type de géométrie
    let layerType: 'line' | 'fill' | 'circle' | 'symbol' = 'line'
    const paint: Record<string, unknown> = {}

    switch (layer.geometryType) {
      case 'line':
      case 'roads':
      case 'ways':
        layerType = 'line'
        paint['line-color'] = layer.defaultStyle?.paint?.['line-color'] || '#3388ff'
        paint['line-width'] = layer.defaultStyle?.paint?.['line-width'] || 1
        paint['line-opacity'] = layer.defaultStyle?.paint?.['line-opacity'] ?? 0.8
        break
      case 'polygon':
        layerType = 'fill'
        paint['fill-color'] = layer.defaultStyle?.paint?.['fill-color'] || '#3388ff'
        paint['fill-opacity'] = layer.defaultStyle?.paint?.['fill-opacity'] ?? 0.3
        if (layer.defaultStyle?.paint?.['line-color']) {
          // Ajouter une couche de contour pour les polygones
          map.addLayer({
            id: `${sourceId}-outline`,
            type: 'line',
            source: sourceId,
            'source-layer': layer.tableName,
            paint: {
              'line-color': layer.defaultStyle.paint['line-color'],
              'line-width': layer.defaultStyle.paint['line-width'] || 1,
              'line-opacity': layer.defaultStyle.paint['line-opacity'] ?? 0.8,
            },
            metadata: { isPersistent: true },
          })
        }
        break
      case 'point':
      case 'nodes':
      case 'users':
        layerType = 'circle'
        paint['circle-color'] = layer.defaultStyle?.paint?.['circle-color'] || '#ff3333'
        paint['circle-radius'] = layer.defaultStyle?.paint?.['circle-radius'] || 4
        paint['circle-opacity'] = layer.defaultStyle?.paint?.['circle-opacity'] ?? 0.8
        break
      default:
        layerType = 'line'
    }

    // Ajouter la couche principale
    const layerSpec: maplibregl.LayerSpecification = {
      id: sourceId,
      type: layerType,
      source: sourceId,
      'source-layer': layer.tableName,
      paint,
      layout: layer.defaultStyle?.layout || {},
      metadata: { isPersistent: true },
    }

    map.addLayer(layerSpec)
  }

  /**
   * Supprime une couche OSM de la carte
   */
  static removeOsmLayerFromMap(myMap: InstanceType<typeof Map> | undefined, layer: OsmLayer): void {
    if (!myMap?.map) {
      return
    }

    const map = myMap.map
    const sourceId = layer.id.toString()

    // Supprimer la couche principale
    if (map.getLayer(sourceId)) {
      map.removeLayer(sourceId)
    }

    // Supprimer la couche de contour si elle existe (pour les polygones)
    const outlineLayerId = `${sourceId}-outline`
    if (map.getLayer(outlineLayerId)) {
      map.removeLayer(outlineLayerId)
    }

    // Supprimer la source
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId)
    }
  }

  /**
   * Met à jour la visibilité d'une couche OSM
   */
  static updateLayerVisibility(
    myMap: InstanceType<typeof Map> | undefined,
    layer: OsmLayer,
    isVisible: boolean,
  ): void {
    if (!myMap?.map) {
      return
    }

    const map = myMap.map
    const sourceId = layer.id.toString()

    if (isVisible) {
      // Ajouter la couche si elle n'existe pas
      if (!map.getLayer(sourceId)) {
        this.addOsmLayerToMap(myMap, layer)
      } else {
        // Sinon, juste changer la visibilité
        map.setLayoutProperty(sourceId, 'visibility', 'visible')
      }
    } else {
      // Masquer la couche
      if (map.getLayer(sourceId)) {
        map.setLayoutProperty(sourceId, 'visibility', 'none')
      }
    }
  }

  /**
   * Met à jour l'opacité d'une couche OSM
   */
  static updateLayerOpacity(
    myMap: InstanceType<typeof Map> | undefined,
    layer: OsmLayer,
    opacity: number,
  ): void {
    if (!myMap?.map) {
      return
    }

    const map = myMap.map
    const sourceId = layer.id.toString()
    const opacityValue = opacity / 100

    if (!map.getLayer(sourceId)) {
      return
    }

    // Mettre à jour l'opacité selon le type de layer
    switch (layer.geometryType) {
      case 'line':
      case 'roads':
      case 'ways':
        map.setPaintProperty(sourceId, 'line-opacity', opacityValue)
        break
      case 'polygon':
        map.setPaintProperty(sourceId, 'fill-opacity', opacityValue)
        const outlineLayerId = `${sourceId}-outline`
        if (map.getLayer(outlineLayerId)) {
          map.setPaintProperty(outlineLayerId, 'line-opacity', opacityValue)
        }
        break
      case 'point':
      case 'nodes':
      case 'users':
        map.setPaintProperty(sourceId, 'circle-opacity', opacityValue)
        break
    }
  }

  /**
   * Ajoute une couche OSM à la carte avec un filtre par catégorie ou sous-catégories
   * Le filtre est appliqué côté serveur via les fonctions PostgreSQL filtrées par catégorie
   *
   * @param myMap - Instance de la carte MapLibre
   * @param layer - Configuration de la couche OSM
   * @param categoryCode - Code de la catégorie (optionnel, utilisé si subCategoryIds n'est pas fourni)
   * @param subCategoryIds - Tableau d'IDs de sous-catégories (optionnel, prioritaire sur categoryCode)
   */
  static async addOsmLayerToMapWithCategoryFilter(
    myMap: InstanceType<typeof Map> | undefined,
    layer: OsmLayer & { categoryCode?: string },
    categoryCode?: string,
    subCategoryIds?: number[],
  ): Promise<void> {
    if (!myMap?.map) {
      console.error("La carte n'est pas initialisée")
      return
    }

    const map = myMap.map
    const sourceId = layer.id.toString()

    // Construire l'URL de base et la fonction de génération d'URL
    // S'assurer que l'URL Martin est initialisée
    if (!this.martinBaseUrl && !layer.martinUrl) {
      this.initialize()
    }

    const baseUrl = layer.martinUrl || this.martinBaseUrl
    if (!baseUrl || baseUrl.trim() === '') {
      console.error("L'URL Martin n'est pas configurée")
      console.error('Vérifiez que VITE_MARTIN_TILES_URL est définie dans votre .env')
      return
    }

    // À ce point, baseUrl est garanti d'être une string non-vide
    const validBaseUrl: string = baseUrl

    const functionNameMap: Record<string, string> = {
      point: 'vector_tile_planet_osm_point_by_category',
      line: 'vector_tile_planet_osm_line_by_category',
      polygon: 'vector_tile_planet_osm_polygon_by_category',
      roads: 'vector_tile_planet_osm_roads_by_category',
    }
    const functionName =
      functionNameMap[layer.geometryType] || 'vector_tile_planet_osm_line_by_category'

    // Construire l'URL avec le paramètre approprié
    // Si subCategoryIds est fourni, utiliser subcategory_ids (tableau JSON d'IDs numériques)
    // Sinon, utiliser category_code
    // Format attendu par Martin:
    //   ?subcategory_ids=[162,163]
    //   ?category_code=transport_routier
    let queryParam = ''
    if (subCategoryIds && subCategoryIds.length > 0) {
      // Encoder le tableau JSON d'IDs numériques pour le paramètre subcategory_ids
      // Exemple: [162, 163] devient %5B162%2C163%5D
      const subCategoryIdsJson = JSON.stringify(subCategoryIds)
      queryParam = `subcategory_ids=${encodeURIComponent(subCategoryIdsJson)}`
    } else if (categoryCode) {
      // Utiliser category_code si fourni
      queryParam = `category_code=${encodeURIComponent(categoryCode)}`
    } else {
      console.error('Aucun code de catégorie ou ID de sous-catégorie fourni')
      return
    }

    // MapLibre utilise un template string avec {z}, {x}, {y} pour les tuiles
    // Format: {baseUrl}/{functionName}/{z}/{x}/{y}?{queryParam}
    // Base URL Martin: http://martin.sigcom.backend
    // Exemple: http://martin.sigcom.backend/vector_tile_planet_osm_line_by_category/{z}/{x}/{y}?subcategory_ids=[162]
    // Note: Ne pas inclure .pbf dans l'URL, Martin le gère automatiquement
    // Nettoyer l'URL de base en enlevant les paramètres de requête comme ?tab=tiles
    // et en supprimant le slash final pour éviter les doubles slashes
    const cleanBaseUrl = validBaseUrl.split('?')[0].replace(/\/+$/, '')
    const tilesUrl = `${cleanBaseUrl}/${functionName}/{z}/{x}/{y}?${queryParam}`

    // Attendre que la carte soit prête AVANT d'ajouter les sources
    if (!map.loaded()) {
      await new Promise<void>((resolve) => {
        map.once('load', () => resolve())
      })
    }

    // Vérifier si la source existe déjà
    if (!map.getSource(sourceId)) {
      // Ajouter la source avec l'URL de tuiles
      try {
        map.addSource(sourceId, {
          type: 'vector',
          tiles: [tilesUrl],
          minzoom: 0,
          maxzoom: 22,
        })
      } catch (error) {
        console.error(`[OsmLayersService] Erreur lors de l'ajout de la source ${sourceId}:`, error)
        return
      }
    } else {
      // Si la source existe déjà, la supprimer et la recréer avec la nouvelle URL
      // Supprimer d'abord les layers associés
      const existingLayer = map.getLayer(sourceId)
      if (existingLayer) {
        map.removeLayer(sourceId)
      }
      const outlineLayerId = `${sourceId}-outline`
      if (map.getLayer(outlineLayerId)) {
        map.removeLayer(outlineLayerId)
      }

      map.removeSource(sourceId)
      map.addSource(sourceId, {
        type: 'vector',
        tiles: [tilesUrl],
        minzoom: 0,
        maxzoom: 22,
      })
    }

    // Déterminer le type de layer selon le type de géométrie
    let layerType: 'line' | 'fill' | 'circle' | 'symbol' = 'line'
    const paint: Record<string, unknown> = {}
    // Plus besoin de filtre côté client, le filtrage se fait côté serveur

    switch (layer.geometryType) {
      case 'line':
      case 'roads':
      case 'ways':
        layerType = 'line'
        paint['line-color'] = layer.defaultStyle?.paint?.['line-color'] || '#3388ff'
        paint['line-width'] = layer.defaultStyle?.paint?.['line-width'] || 1
        paint['line-opacity'] = layer.defaultStyle?.paint?.['line-opacity'] ?? 0.8
        break
      case 'polygon':
        layerType = 'fill'
        paint['fill-color'] = layer.defaultStyle?.paint?.['fill-color'] || '#3388ff'
        paint['fill-opacity'] = layer.defaultStyle?.paint?.['fill-opacity'] ?? 0.3
        if (layer.defaultStyle?.paint?.['line-color']) {
          // Ajouter une couche de contour pour les polygones
          // Plus besoin de filtre, les données sont déjà filtrées côté serveur
          // Utiliser le nom de la fonction comme source-layer car on utilise les fonctions filtrées
          const outlineLayerId = `${sourceId}-outline`
          map.addLayer({
            id: outlineLayerId,
            type: 'line',
            source: sourceId,
            'source-layer': functionName,
            paint: {
              'line-color': layer.defaultStyle.paint['line-color'],
              'line-width': layer.defaultStyle.paint['line-width'] || 1,
              'line-opacity': layer.defaultStyle.paint['line-opacity'] ?? 0.8,
            },
            layout: {
              visibility: 'visible',
            },
            metadata: { isPersistent: true },
          })
          // S'assurer que le layer outline est visible
          map.setLayoutProperty(outlineLayerId, 'visibility', 'visible')
        }
        break
      case 'point':
      case 'nodes':
      case 'users':
        layerType = 'circle'
        paint['circle-color'] = layer.defaultStyle?.paint?.['circle-color'] || '#ff3333'
        paint['circle-radius'] = layer.defaultStyle?.paint?.['circle-radius'] || 4
        paint['circle-opacity'] = layer.defaultStyle?.paint?.['circle-opacity'] ?? 0.8
        break
      default:
        layerType = 'line'
    }

    // Ajouter la couche principale (sans filtre côté client)
    // Utiliser le nom de la fonction comme source-layer car on utilise les fonctions filtrées
    const layerSpec: maplibregl.LayerSpecification = {
      id: sourceId,
      type: layerType,
      source: sourceId,
      'source-layer': functionName,
      paint,
      layout: {
        visibility: 'visible',
        ...(layer.defaultStyle?.layout || {}),
      },
      metadata: { isPersistent: true },
    }

    // Supprimer la couche si elle existe déjà pour la recréer
    if (map.getLayer(sourceId)) {
      map.removeLayer(sourceId)
    }

    try {
      map.addLayer(layerSpec)

      // S'assurer que le layer est visible
      map.setLayoutProperty(sourceId, 'visibility', 'visible')

      // Forcer le rechargement des tuiles
      map.triggerRepaint()
    } catch (error) {
      console.error(`[OsmLayersService] Erreur lors de l'ajout du layer ${sourceId}:`, error)
    }
  }

  /**
   * Ajoute une couche OSM à la carte avec plusieurs sous-catégories regroupées
   * Utile pour afficher plusieurs sous-catégories avec le même style dans une seule couche
   *
   * @param myMap - Instance de la carte MapLibre
   * @param layer - Configuration de la couche OSM
   * @param subCategoryIds - Tableau d'IDs de sous-catégories à regrouper
   */
  static async addOsmLayerToMapWithMultipleSubCategories(
    myMap: InstanceType<typeof Map> | undefined,
    layer: OsmLayer,
    subCategoryIds: number[],
  ): Promise<void> {
    if (!subCategoryIds || subCategoryIds.length === 0) {
      console.error('Aucun ID de sous-catégorie fourni')
      return
    }

    return this.addOsmLayerToMapWithCategoryFilter(myMap, layer, undefined, subCategoryIds)
  }

  /**
   * Retourne le nom de la source-layer selon le type de géométrie
   * Les fonctions filtrées utilisent le nom de la fonction comme source-layer
   */
  private static getSourceLayerName(geometryType: string, categoryCode?: string): string {
    // Si categoryCode est fourni, utiliser les fonctions filtrées
    if (categoryCode) {
      const sourceLayerMap: Record<string, string> = {
        point: 'vector_tile_planet_osm_point_by_category',
        line: 'vector_tile_planet_osm_line_by_category',
        polygon: 'vector_tile_planet_osm_polygon_by_category',
        roads: 'vector_tile_planet_osm_roads_by_category',
      }
      return sourceLayerMap[geometryType] || 'vector_tile_planet_osm_line_by_category'
    }

    // Sinon, utiliser le nom de table standard
    const tableNameMap: Record<string, string> = {
      point: 'planet_osm_point',
      line: 'planet_osm_line',
      polygon: 'planet_osm_polygon',
      roads: 'planet_osm_roads',
    }
    return tableNameMap[geometryType] || 'planet_osm_line'
  }

  /**
   * Initialise les couches de catégories OSM depuis l'API
   * Charge les catégories depuis l'API et les transforme en format de couches
   * @param cmnCode When set, scopes the request to a commune (same contract as
   *   AtlasCategoriesService.getAllCategories). Omit for the national scope.
   * @returns Promise<Array<{ mainLayer: Layer; subLayers: Layer[] }>>
   */
  static async initOsmLayers(
    cmnCode?: string | null,
  ): Promise<Array<{ mainLayer: Layer; subLayers: Layer[] }>> {
    try {
      const categories = await AtlasCategoriesService.getAllCategories(false, cmnCode)

      const categoriesWithData = categories.filter((category: AtlasCategory) => {
        return (category.subCategories ?? []).some((sub) => sub.hasData === true)
      })

      return categoriesWithData.map((category: AtlasCategory) => {
        const subCategoriesWithData = (category.subCategories ?? []).filter(
          (sub) => sub.hasData === true,
        )

        const mainLayer = LayerService.initLayer(
          {
            id: `osm-category-${category.id}`,
            name: category.name,
            isShown: false,
          },
          false,
        )

        const subLayers = subCategoriesWithData.map((sub) =>
          LayerService.initLayer(
            {
              id: `osm-subcategory-${sub.id}`,
              name: libelleDynamique(sub.nameFr, sub.nameEn),
              isShown: false,
            },
            false,
          ),
        )

        return { mainLayer, subLayers }
      })
    } catch (error) {
      console.error("Error initializing OSM layers:", error)
      return []
    }
  }
}
